import type { CurrencyFormat } from "@rubyplay/effw-connector";
import { debugError, debugLog, debugWarn } from "@/utils/debug";
import { TooltipPosition } from "@/integrations/captainup/CaptainUpTypes";

export type CaptainUpProtocolOutboundType =
	| "cap.g2c.betUpdated"
	| "cap.g2c.promotionStarted"
	| "cap.g2c.promotionEnded"
	| "cap.g2c.buyFeatureStarted"
	| "cap.g2c.buyFeatureEnded"
	| "cap.g2c.paidRoundStarted"
	| "cap.g2c.paidRoundEnded"
	| "cap.g2c.roundStarted"
	| "cap.g2c.roundEnded"
	| "cap.g2c.animationsStarted"
	| "cap.g2c.animationsEnded"
	| "cap.g2c.gameReady"
    | "cap.g2c.setTooltipPosition";

export type CaptainUpProtocolInboundType =
	| "cap.c2g.pause"
	| "cap.c2g.resume"
	| "cap.c2g.onUserInteraction"
	| "cap.c2g.onEvent";

type CaptainUpProtocolBase = {
	channel: "captainup-browser-protocol";
	version: "1.0";
};

export type CaptainUpProtocolOutboundMessage =
	| (CaptainUpProtocolBase & {
			type: "cap.g2c.betUpdated";
			payload: {
				bet: number;
				currencyFormat?: CurrencyFormat;
			};
	  })
	| (CaptainUpProtocolBase & {
			type:
				| "cap.g2c.promotionStarted"
				| "cap.g2c.promotionEnded"
				| "cap.g2c.buyFeatureStarted"
				| "cap.g2c.buyFeatureEnded"
				| "cap.g2c.paidRoundStarted"
				| "cap.g2c.paidRoundEnded"
				| "cap.g2c.roundStarted"
				| "cap.g2c.roundEnded"
				| "cap.g2c.animationsStarted"
				| "cap.g2c.animationsEnded"
				| "cap.g2c.gameReady";
			payload: Record<string, never>;
	  })
    | (CaptainUpProtocolBase & {
            type: "cap.g2c.setTooltipPosition";
            payload: {
                tooltipPosition: TooltipPosition;
            };
	  });

export type CaptainUpProtocolInboundMessage =
	| (CaptainUpProtocolBase & {
			type: "cap.c2g.pause" | "cap.c2g.resume";
			payload: Record<string, never>;
	  })
	| (CaptainUpProtocolBase & {
			type: "cap.c2g.onUserInteraction";
			payload: Record<string, unknown>;
	  })
	| (CaptainUpProtocolBase & {
			type: "cap.c2g.onEvent";
			payload: {
				type: string;
				entity: unknown;
			};
	  });

export type CaptainUpProtocolMessage =
	| CaptainUpProtocolOutboundMessage
	| CaptainUpProtocolInboundMessage;

export type CaptainUpPauseResumeHandler = (
	type: "cap.c2g.pause" | "cap.c2g.resume"
) => void;

export type CaptainUpUserInteractionHandler = (
	action: string,
	data: unknown
) => void;

export type CaptainUpEventHandler = (
	type: string,
	entity: unknown
) => void;

type SubscriptionRecord = {
	pauseResumeHandlers: Set<CaptainUpPauseResumeHandler>;
	userInteractionHandlers: Set<CaptainUpUserInteractionHandler>;
	eventHandlers: Set<CaptainUpEventHandler>;
};

const PROTOCOL_CHANNEL = "captainup-browser-protocol";
const PROTOCOL_VERSION = "1.0";

type NormalizedInboundMessage =
	| {
			type: "cap.c2g.pause" | "cap.c2g.resume";
			payload: Record<string, never>;
	  }
	| {
			type: "cap.c2g.onUserInteraction";
			payload: Record<string, unknown>;
	  }
	| {
			type: "cap.c2g.onEvent";
			payload: {
				type: string;
				entity: unknown;
			};
	  };

function isObjectRecord(value: unknown): value is Record<string, unknown> {
	return !!value && typeof value === "object";
}

function isEmptyPayload(value: unknown): value is Record<string, never> {
	if (!isObjectRecord(value)) {
		return false;
	}

	return Object.keys(value).length === 0;
}

function isOnEventPayload(
	value: unknown
): value is { type: string; entity: unknown } {
	if (!isObjectRecord(value)) {
		return false;
	}

	if (typeof value.type !== "string" || value.type.trim().length === 0) {
		return false;
	}

	return "entity" in value;
}

function isCurrentWindowSource(source: MessageEventSource | null): boolean {
	return source === window;
}

function isSameOrigin(origin: string): boolean {
	if (origin === window.location.origin) {
		return true;
	}

	if (origin === "null") {
		return true;
	}

	return false;
}

function isEnvelopeMessage(value: unknown): value is Record<string, unknown> {
	if (!isObjectRecord(value)) {
		return false;
	}

	if (value.channel !== PROTOCOL_CHANNEL) {
		return false;
	}

	if (value.version !== PROTOCOL_VERSION) {
		return false;
	}

	if (typeof value.type !== "string") {
		return false;
	}

	if (!("payload" in value)) {
		return false;
	}

	return true;
}

function isOutboundProtocolType(value: unknown): value is CaptainUpProtocolOutboundType {
	if (typeof value !== "string") {
		return false;
	}

	return (
		value === "cap.g2c.betUpdated" ||
		value === "cap.g2c.promotionStarted" ||
		value === "cap.g2c.promotionEnded" ||
		value === "cap.g2c.buyFeatureStarted" ||
		value === "cap.g2c.buyFeatureEnded" ||
		value === "cap.g2c.paidRoundStarted" ||
		value === "cap.g2c.paidRoundEnded" ||
		value === "cap.g2c.roundStarted" ||
		value === "cap.g2c.roundEnded" ||
		value === "cap.g2c.animationsStarted" ||
		value === "cap.g2c.animationsEnded" ||
		value === "cap.g2c.gameReady" ||
		value === "cap.g2c.setTooltipPosition"
	);
}

function isInboundProtocolType(value: unknown): value is CaptainUpProtocolInboundType {
	if (typeof value !== "string") {
		return false;
	}

	return (
		value === "cap.c2g.pause" ||
		value === "cap.c2g.resume" ||
		value === "cap.c2g.onUserInteraction" ||
		value === "cap.c2g.onEvent"
	);
}

function normalizeEnvelopeInboundMessage(
	value: Record<string, unknown>
): NormalizedInboundMessage | null {
	if (!isInboundProtocolType(value.type)) {
		return null;
	}

	switch (value.type) {
		case "cap.c2g.pause":
		case "cap.c2g.resume": {
			if (!isEmptyPayload(value.payload)) {
				return null;
			}

			return {
				type: value.type,
				payload: {},
			};
		}

		case "cap.c2g.onUserInteraction": {
			if (!isObjectRecord(value.payload)) {
				return null;
			}

			return {
				type: "cap.c2g.onUserInteraction",
				payload: value.payload,
			};
		}

		case "cap.c2g.onEvent": {
			if (!isOnEventPayload(value.payload)) {
				return null;
			}

			return {
				type: "cap.c2g.onEvent",
				payload: value.payload,
			};
		}
	}
}

function normalizeBareInboundMessage(
	value: Record<string, unknown>
): NormalizedInboundMessage | null {
	if (!isInboundProtocolType(value.type)) {
		return null;
	}

	switch (value.type) {
		case "cap.c2g.pause":
		case "cap.c2g.resume":
			return {
				type: value.type,
				payload: {},
			};

		case "cap.c2g.onUserInteraction":
			return {
				type: "cap.c2g.onUserInteraction",
				payload: value,
			};

		case "cap.c2g.onEvent": {
			const eventType = value.eventType ?? value.name ?? value.action ?? value.type;
			const entity = "entity" in value ? value.entity : value.data;

			if (typeof eventType !== "string" || eventType.trim().length === 0) {
				return null;
			}

			return {
				type: "cap.c2g.onEvent",
				payload: {
					type: eventType,
					entity,
				},
			};
		}
	}
}

function normalizeInboundProtocolMessage(value: unknown): NormalizedInboundMessage | null {
	if (!isObjectRecord(value)) {
		return null;
	}

	if (isEnvelopeMessage(value)) {
		return normalizeEnvelopeInboundMessage(value);
	}

	return normalizeBareInboundMessage(value);
}

class CaptainUpMessagingProtocol {
	private readonly subscriptions: SubscriptionRecord = {
		pauseResumeHandlers: new Set(),
		userInteractionHandlers: new Set(),
		eventHandlers: new Set(),
	};

	private listening = false;

	public sendBetUpdated(bet: number, currencyFormat?: CurrencyFormat): void {
		this.postMessage({
			channel: PROTOCOL_CHANNEL,
			version: PROTOCOL_VERSION,
			type: "cap.g2c.betUpdated",
			payload: {
				bet,
				...(currencyFormat ? { currencyFormat } : {}),
			},
		});
	}

    public sendSetTooltipPosition(tooltipPosition: TooltipPosition): void {
        this.postMessage({
            channel: PROTOCOL_CHANNEL,
            version: PROTOCOL_VERSION,
            type: "cap.g2c.setTooltipPosition",
            payload: {
                tooltipPosition,
            },
        });
    }

	public sendGameReady(): void {
		this.postMessage({
			channel: PROTOCOL_CHANNEL,
			version: PROTOCOL_VERSION,
			type: "cap.g2c.gameReady",
			payload: {},
		});
	}

	public sendPromotionStarted(): void {
		this.postMessage({
			channel: PROTOCOL_CHANNEL,
			version: PROTOCOL_VERSION,
			type: "cap.g2c.promotionStarted",
			payload: {},
		});
	}

	public sendPromotionEnded(): void {
		this.postMessage({
			channel: PROTOCOL_CHANNEL,
			version: PROTOCOL_VERSION,
			type: "cap.g2c.promotionEnded",
			payload: {},
		});
	}

	public sendBuyFeatureStarted(): void {
		this.postMessage({
			channel: PROTOCOL_CHANNEL,
			version: PROTOCOL_VERSION,
			type: "cap.g2c.buyFeatureStarted",
			payload: {},
		});
	}

	public sendBuyFeatureEnded(): void {
		this.postMessage({
			channel: PROTOCOL_CHANNEL,
			version: PROTOCOL_VERSION,
			type: "cap.g2c.buyFeatureEnded",
			payload: {},
		});
	}

	public sendPaidRoundStarted(): void {
		this.postMessage({
			channel: PROTOCOL_CHANNEL,
			version: PROTOCOL_VERSION,
			type: "cap.g2c.paidRoundStarted",
			payload: {},
		});
	}

	public sendPaidRoundEnded(): void {
		this.postMessage({
			channel: PROTOCOL_CHANNEL,
			version: PROTOCOL_VERSION,
			type: "cap.g2c.paidRoundEnded",
			payload: {},
		});
	}

	public sendRoundStarted(): void {
		this.postMessage({
			channel: PROTOCOL_CHANNEL,
			version: PROTOCOL_VERSION,
			type: "cap.g2c.roundStarted",
			payload: {},
		});
	}

	public sendRoundEnded(): void {
		this.postMessage({
			channel: PROTOCOL_CHANNEL,
			version: PROTOCOL_VERSION,
			type: "cap.g2c.roundEnded",
			payload: {},
		});
	}

	public sendAnimationsStarted(): void {
		this.postMessage({
			channel: PROTOCOL_CHANNEL,
			version: PROTOCOL_VERSION,
			type: "cap.g2c.animationsStarted",
			payload: {},
		});
	}

	public sendAnimationsEnded(): void {
		this.postMessage({
			channel: PROTOCOL_CHANNEL,
			version: PROTOCOL_VERSION,
			type: "cap.g2c.animationsEnded",
			payload: {},
		});
	}

	public onPauseResume(handler: CaptainUpPauseResumeHandler): () => void {
		this.ensureListening();
		this.subscriptions.pauseResumeHandlers.add(handler);

		debugLog("[CaptainUpProtocol] pause/resume handler subscribed", {
			count: this.subscriptions.pauseResumeHandlers.size,
		});

		return () => {
			this.subscriptions.pauseResumeHandlers.delete(handler);

			debugLog("[CaptainUpProtocol] pause/resume handler unsubscribed", {
				count: this.subscriptions.pauseResumeHandlers.size,
			});
		};
	}

	public onUserInteraction(handler: CaptainUpUserInteractionHandler): () => void {
		this.ensureListening();
		this.subscriptions.userInteractionHandlers.add(handler);

		debugLog("[CaptainUpProtocol] user interaction handler subscribed", {
			count: this.subscriptions.userInteractionHandlers.size,
		});

		return () => {
			this.subscriptions.userInteractionHandlers.delete(handler);

			debugLog("[CaptainUpProtocol] user interaction handler unsubscribed", {
				count: this.subscriptions.userInteractionHandlers.size,
			});
		};
	}

	public onEvent(handler: CaptainUpEventHandler): () => void {
		this.ensureListening();
		this.subscriptions.eventHandlers.add(handler);

		debugLog("[CaptainUpProtocol] event handler subscribed", {
			count: this.subscriptions.eventHandlers.size,
		});

		return () => {
			this.subscriptions.eventHandlers.delete(handler);

			debugLog("[CaptainUpProtocol] event handler unsubscribed", {
				count: this.subscriptions.eventHandlers.size,
			});
		};
	}

	private ensureListening(): void {
		if (this.listening) {
			return;
		}

		window.addEventListener("message", this.handleWindowMessage);
		this.listening = true;

		debugLog("[CaptainUpProtocol] message listener attached", {
			channel: PROTOCOL_CHANNEL,
			version: PROTOCOL_VERSION,
			windowOrigin: window.location.origin,
		});
	}

	private postMessage(message: CaptainUpProtocolOutboundMessage): void {
		debugLog("[CaptainUpProtocol] postMessage -> CaptainUp", {
			message,
			targetOrigin: window.location.origin,
			windowOrigin: window.location.origin,
		});

		try {
			window.postMessage(message, window.location.origin);
		} catch (error) {
			debugError("[CaptainUpProtocol] failed to post message", {
				type: message.type,
				error,
			});
		}
	}

	private handleWindowMessage = (event: MessageEvent): void => {
		const rawData = event.data;

		const looksProtocolLike =
			isObjectRecord(rawData) &&
			(typeof rawData.channel === "string" ||
				typeof rawData.type === "string" ||
				typeof rawData.version === "string");

		if (looksProtocolLike) {
			debugLog("[CaptainUpProtocol] raw window message observed", {
				origin: event.origin,
				isCurrentWindowSource: isCurrentWindowSource(event.source),
				isSameOrigin: isSameOrigin(event.origin),
				data: rawData,
			});
		}

		if (!isCurrentWindowSource(event.source)) {
			return;
		}

		if (!isSameOrigin(event.origin)) {
			return;
		}

		if (isObjectRecord(rawData) && isOutboundProtocolType(rawData.type)) {
			debugLog("[CaptainUpProtocol] ignoring self outbound echo", {
				type: rawData.type,
			});
			return;
		}

		const message = normalizeInboundProtocolMessage(rawData);

		if (!message) {
			if (looksProtocolLike) {
				debugWarn("[CaptainUpProtocol] ignored window message because it could not be normalized", {
					data: rawData,
				});
			}
			return;
		}

		debugLog("[CaptainUpProtocol] postMessage <- CaptainUp", message);

		switch (message.type) {
			case "cap.c2g.pause":
			case "cap.c2g.resume": {
				for (const handler of this.subscriptions.pauseResumeHandlers) {
					try {
						handler(message.type);
					} catch (error) {
						debugWarn("[CaptainUpProtocol] pause/resume handler failed", error);
					}
				}
				return;
			}

			case "cap.c2g.onUserInteraction": {
				const payload = message.payload;

				const action =
					typeof payload.action === "string"
						? payload.action
						: typeof payload.event === "string"
							? payload.event
							: typeof payload.type === "string"
								? payload.type
								: "";

				if (!action) {
					debugWarn("[CaptainUpProtocol] missing action in onUserInteraction", payload);
					return;
				}

				const data = "data" in payload ? payload.data : undefined;

				for (const handler of this.subscriptions.userInteractionHandlers) {
					try {
						handler(action, data);
					} catch (error) {
						debugWarn("[CaptainUpProtocol] onUserInteraction handler failed", error);
					}
				}
				return;
			}

			case "cap.c2g.onEvent": {
				for (const handler of this.subscriptions.eventHandlers) {
					try {
						handler(message.payload.type, message.payload.entity);
					} catch (error) {
						debugWarn("[CaptainUpProtocol] onEvent handler failed", error);
					}
				}
				return;
			}
		}
	};
}

let singleton: CaptainUpMessagingProtocol | null = null;

export function getCaptainUpMessagingProtocol(): CaptainUpMessagingProtocol {
	if (!singleton) {
		singleton = new CaptainUpMessagingProtocol();
	}
	return singleton;
}